import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { AlertCircle, Pen } from 'lucide-react';
import { getUserMistakes } from '../utils/mistakesAPI';
import { saveNote as saveMistakeNote } from '../utils/notesAPI';

function Mistakes({ session }) {
  const [mistakes, setMistakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMistakes = async () => {
      try {
        setLoading(true);
        
        // 获取用户ID
        const userId = localStorage.getItem('user_id');
        
        if (!userId && !session?.user?.id) {
          // 未登录，重定向到登录页
          navigate('/login');
          return;
        }
        
        // 使用mistakesAPI获取错题
        const mistakesData = await getUserMistakes(userId || session?.user?.id);
        
        // 处理错题数据
        const processedMistakes = [];
        
        for (const mistake of mistakesData) {
          // 处理题目和选项
          const questionData = mistake.question || {};
          const optionsData = mistake.options || {};
          
          // 获取用户答案 - 从question对象中获取
          const userAnswer = questionData.userAnswer || '';
          
          // 构建标准格式的题目对象
          const processedMistake = {
            _id: mistake._id,
            title: questionData.title || '未知题目',
            options: processOptionsData(optionsData),
            userAnswer: userAnswer,
            correctAnswer: findCorrectAnswer(optionsData),
            comments: questionData.comments || '无解析',
            examId: questionData.examid || '',
            ordernum: mistake.ordernum || ''
          };
          
          processedMistakes.push(processedMistake);
        }
        
        setMistakes(processedMistakes);
        
      } catch (error) {
        console.error('Error fetching mistakes:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMistakes();
  }, [session, navigate]);

  // 处理选项数据
  const processOptionsData = (options) => {
    if (Array.isArray(options)) {
      return options;
    } else if (typeof options === 'object') {
      return Object.entries(options).map(([code, content]) => {
        if (typeof content === 'object') {
          return {
            code,
            content: content.content || content.text || '',
            value: content.value || content.isCorrect ? "1" : "0"
          };
        } else {
          return { code, content, value: "0" };
        }
      });
    }
    return [];
  };

  // 查找正确答案
  const findCorrectAnswer = (options) => {
    if (Array.isArray(options)) {
      const correctOption = options.find(opt => opt.value === "1" || opt.isCorrect);
      return correctOption ? correctOption.code : '';
    } else if (typeof options === 'object') {
      for (const [code, content] of Object.entries(options)) {
        if ((typeof content === 'object' && (content.value === "1" || content.isCorrect)) ||
            content === "1") {
          return code;
        }
      }
    }
    return '';
  };

  // 保存笔记
  const saveNote = async (questionId, note) => {
    try {
      const userId = localStorage.getItem('user_id') || session?.user?.id;
      
      if (!userId) return;
      
      const result = await saveMistakeNote(userId, questionId, note);
      
      if (result.success) {
        alert('笔记已保存');
      } else {
        throw new Error(result.error);
      }
      
    } catch (error) {
      console.error('Error saving note:', error.message);
      alert('保存笔记失败: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <Header title="错题本" />
      
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-md font-medium text-red-800">错题提示</h3>
              <div className="text-sm text-red-700 mt-1">
                这里收集了您在考试中回答错误的题目，系统会在考试完成后自动保存您的错题。认真学习这些题目可以帮助您更好地掌握知识点。
              </div>
            </div>
          </div>
        </div>
        
        {mistakes.length > 0 ? (
          <div className="space-y-6">
            {mistakes.map((mistake, index) => (
              <div key={`${mistake._id}-${index}`} className="bg-white rounded-2xl shadow-md p-5">
                <div className="border-b border-gray-100 pb-4 mb-4">
                  <div className="flex items-start">
                    <span className="bg-red-100 text-red-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3 flex-shrink-0">
                      {index + 1}
                    </span>
                    <h3 className="text-lg font-medium text-gray-800">{mistake.title}</h3>
                  </div>
                </div>
                
                <div className="mb-4 space-y-3">
                  {mistake.options.map((option) => (
                    <div 
                      key={option.code}
                      className={`p-3 rounded-lg border ${
                        option.code === mistake.correctAnswer
                          ? 'border-green-500 bg-green-50'
                          : option.code === mistake.userAnswer
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                          option.code === mistake.correctAnswer
                            ? 'bg-green-500 text-white'
                            : option.code === mistake.userAnswer
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-500'
                        }`}>
                          {option.code}
                        </div>
                        <div className="text-gray-800">{option.content}</div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                  <h4 className="font-medium text-yellow-800 mb-1">解析</h4>
                  <p className="text-sm text-yellow-700">{mistake.comments || '暂无解析'}</p>
                </div>
                
                <div className="flex justify-end">
                  <div className="relative inline-block text-left">
                    <button
                      className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-medium rounded-md focus:outline-none"
                      onClick={() => {
                        const note = prompt('请输入笔记:');
                        if (note) saveNote(mistake._id, note);
                      }}
                    >
                      <Pen className="mr-2 h-4 w-4" />
                      添加笔记
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-1">暂无错题</h3>
            <p className="text-gray-500">您还没有任何错题，去答题看看吧！</p>
          </div>
        )}
      </div>
      
      <TabBar />
    </div>
  );
}

export default Mistakes; 